import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { format, isValid } from 'date-fns';
import {
  CLEPeriod,
  CreditTrackerUpdateRequest,
  ICLEPeriod,
  IComplianceGroup,
  ReportingPeriodType,
  UserTrackerData,
  UserTrackerDataRequest
} from '@/@types/client-api';
import useCreditTracker from '@/components/ui/CreditTracker/hooks/useCreditTrackerHook';
import { ModalProps } from '@/components/ui/Modal/Modal';
import { findInRecordCollection } from '@/utils/recordHelpers';
import {
  useRefetchCreditTrackerMutation,
  useUpdateComplianceDateRangeMutation,
  useUpdateUserCleOptionsMutation,
  useSwitchStatusLazyMutation
} from '@/redux/api/client/creditTracker';
import { selectActiveUserTrackerData } from '@/redux/selectors/creditTrackerSelectors';
import useToast from '@/hooks/useToast';
import {
  ComplianceFieldNames,
  SpecialStatusSelector
} from '@/components/ui/CreditTracker/util/constants';
import {
  findSelectedClePeriod,
  getActiveCompliancePeriodValue,
  periodIsMissingComplianceInformation
} from '../util/TrackerHelpers';
import { ComplianceFormFields } from '@/components/ui/CreditTracker/Forms/ComplianceForm';
import useComplianceFormValidation from '@/components/ui/CreditTracker/hooks/validation/useComplianceFormValidationHook';
import {
  compareDates,
  getPeriodAdmitDate,
  getPeriodEndDate,
  getPeriodStartDate,
  getUserBirthDate,
  isUsingDefaultDates
} from '@/components/ui/CreditTracker/util/trackerDateHelpers';
import usePrevious from '@/hooks/usePrevious';
import { CreditTrackerContext } from '@/components/blocks/CreditTracker/CreditTrackerContext';
import { compareObjects } from '@/utils/helpers';

export interface UseComplianceModalProps extends ModalProps {
  customerSk: number;
  complianceFormFields?: ComplianceFormFields;
  birthDate?: Date;
}

const useComplianceModal = ({
  customerSk,
  complianceFormFields,
  setOpen
}: UseComplianceModalProps) => {
  const { transitionOptions, allSpecialStatuses } = useCreditTracker({
    skipQuery: true
  });

  const { isMissingCompliancePeriod, lastTrackerRequest, setIsLoading } =
    useContext(CreditTrackerContext);

  const activelyViewingUserSk = useMemo(
    () => customerSk ?? (lastTrackerRequest?.individualSk ? lastTrackerRequest.individualSk : -1),
    [customerSk, lastTrackerRequest?.individualSk]
  );

  const selectedRegionSelector = useMemo(
    () => selectActiveUserTrackerData(activelyViewingUserSk),
    [activelyViewingUserSk]
  );
  const _selectedRegion = useSelector(selectedRegionSelector);
  const [updateUserOptions] = useUpdateUserCleOptionsMutation();
  const [updateComplianceDateRange] = useUpdateComplianceDateRangeMutation();
  const [getComplianceStartEndDate] = useRefetchCreditTrackerMutation();
  const [switchStatusLazy] = useSwitchStatusLazyMutation();
  const { showSuccessMessage, showFailMessage } = useToast();
  const [isSubmitting, setIsSubmitting] = useState<boolean>();
  const [isUpdatingDateRange, setIsUpdatingDateRange] = useState<boolean>();
  const [isUpdatingForm, setIsUpdatingForm] = useState<boolean>();
  const [statuses, setStatuses] = useState<SpecialStatusSelector[]>(allSpecialStatuses ?? []);
  const [showSpecialStatusError, setShowSpecialStatusError] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [selectedRegion, setSelectedRegion] = useState(_selectedRegion ?? {});
  useEffect(() => {
    const nextSelectedRegion = _selectedRegion ?? {};
    setSelectedRegion(prev => {
      return compareObjects(prev, nextSelectedRegion) ? prev : nextSelectedRegion;
    });
  }, [_selectedRegion]);

  useEffect(() => {
    const nextStatuses = allSpecialStatuses ?? [];
    setStatuses(prev => {
      return JSON.stringify(prev) === JSON.stringify(nextStatuses) ? prev : nextStatuses;
    });
  }, [allSpecialStatuses]);

  const emptyComplianceFormFields: ComplianceFormFields = useMemo(
    () =>
      ({
        complianceGroup: null,
        birthDay: undefined,
        birthMonth: undefined,
        admitYear: undefined,
        admitDate: undefined,
        startDate: '',
        endDate: '',
        specialStatus: null
      }) as ComplianceFormFields,
    []
  );

  const selectedClePeriod = useMemo(
    () => findSelectedClePeriod(selectedRegion?.jurisdiction?.clePeriod),
    [selectedRegion?.jurisdiction?.clePeriod]
  );

  const reportingPeriod = useMemo(
    () => selectedRegion?.jurisdiction?.jurisdictionReportingPeriod?.reportingPeriodType,
    [selectedRegion?.jurisdiction?.jurisdictionReportingPeriod?.reportingPeriodType]
  );

  const [compliancePeriod, setCompliancePeriod] =
    useState<ComplianceFormFields>(emptyComplianceFormFields);

  const initialMount = useRef(true);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      if (complianceFormFields) {
        setCompliancePeriod(complianceFormFields);
      }
    }
  }, [complianceFormFields]);

  const specialStatusChanged = useMemo(() => {
    return (
      allSpecialStatuses?.find(a => a.value?.toLowerCase() === 'true')?.text !=
      statuses?.find(a => a.value?.toLowerCase() === 'true')?.text
    );
  }, [allSpecialStatuses, statuses]);

  const isTransitionalOrNewlyAdmittedRegion = useMemo(() => {
    return selectedRegion?.jurisdiction?.isTransitionalOrNewlyAdmitted ?? false;
  }, [selectedRegion?.jurisdiction?.isTransitionalOrNewlyAdmitted]);

  useEffect(() => {
    if (statuses && transitionOptions?.length) {
      if (!specialStatusChanged) return;

      const flag = transitionOptions.find(a => {
        return [...(a.activatesFlags ?? []), ...(a.removesFlags ?? [])]?.some(
          a => a.toLowerCase().indexOf('period') < 0
        );
      });
      const nextRuleSetFlag = flag?.activatesFlags;

      setCompliancePeriod(prev => {
        if (JSON.stringify(prev?.ruleSetFlag ?? []) === JSON.stringify(nextRuleSetFlag ?? [])) {
          return prev;
        }

        return {
          ...prev,
          ruleSetFlag: nextRuleSetFlag
        };
      });
    }
  }, [allSpecialStatuses, specialStatusChanged, statuses, transitionOptions]);

  useEffect(() => {
    if (!complianceFormFields) return;

    setCompliancePeriod(prev => {
      const isEmptyState = JSON.stringify(prev) === JSON.stringify(emptyComplianceFormFields);
      if (!isEmptyState) return prev;

      return JSON.stringify(prev) === JSON.stringify(complianceFormFields)
        ? prev
        : complianceFormFields;
    });
  }, [complianceFormFields, emptyComplianceFormFields]);

  const previous = usePrevious(compliancePeriod);

  useEffect(() => {
    if (
      previous &&
      compliancePeriod &&
      JSON.stringify(previous) !== JSON.stringify(emptyComplianceFormFields) &&
      JSON.stringify(compliancePeriod) !== JSON.stringify(emptyComplianceFormFields) &&
      (compareDates(previous?.startDate ?? '', compliancePeriod?.startDate ?? '') !== 0 ||
        compareDates(previous?.endDate ?? '', compliancePeriod?.endDate ?? '') !== 0)
    ) {
      const isUserSet =
        compareDates(
          compliancePeriod?.startDate ?? '',
          compliancePeriod?.defaultStartDate ?? ''
        ) !== 0 ||
        compareDates(compliancePeriod?.endDate ?? '', compliancePeriod?.defaultEndDate ?? '') !== 0;

      setCompliancePeriod(prev => {
        if (prev?.isUserSet === isUserSet) return prev;

        return {
          ...prev,
          isUserSet: isUserSet
        };
      });
    }
  }, [compliancePeriod, emptyComplianceFormFields, previous]);

  const selectedCompliancePeriod = useMemo(
    () => getActiveCompliancePeriodValue(selectedRegion),
    [selectedRegion]
  );

  const { handleValidation, fieldsWithErrors, setFieldsWithErrors } = useComplianceFormValidation({
    compliancePeriod,
    reportingPeriod,
    selectedCompliancePeriod
  });

  const isUserSet = useMemo(() => {
    return compliancePeriod?.isUserSet;
  }, [compliancePeriod?.isUserSet]);

  const updateSpecialStatus = useCallback(async () => {
    if (specialStatusChanged) {
      setIsUpdatingForm(true);
      setIsUpdatingDateRange(true);

      const flag = selectedRegion?.jurisdiction?.cleStateTransitionOptions?.find(a => {
        return ![...(a.activatesFlags ?? []), ...(a.removesFlags ?? [])]?.some(
          a => a.toLowerCase().indexOf('period') >= 0
        );
      });
      const currentPeriod: CLEPeriod = {
        ...selectedRegion?.jurisdiction?.clePeriod,
        ruleSetFlag: flag?.activatesFlags
      };
      try {
        await switchStatusLazy({
          customerId: selectedRegion?.customerId,
          jurisdictionId: selectedRegion?.creditRegion_SK,
          jurisdictionKey: selectedRegion?.jurisdiction?.key,
          period: currentPeriod
        } as CreditTrackerUpdateRequest);

        const response = await getComplianceStartEndDate([
          { ...lastTrackerRequest, individualSk: activelyViewingUserSk }
        ] as UserTrackerDataRequest[]);

        const foundTrackerData = findInRecordCollection(
          response.data?.trackerUsers ?? [],
          (x: UserTrackerData) =>
            x.creditRegionShortDescription == selectedRegion?.creditRegionShortDescription
        );

        foundTrackerData && setSelectedRegion({ ...foundTrackerData });

        return foundTrackerData;
      } catch (error) {
        showFailMessage('There was an error updating compliance period');
      } finally {
        setIsUpdatingForm(false);
        setIsUpdatingDateRange(false);
      }
    }
  }, [
    specialStatusChanged,
    selectedRegion?.jurisdiction?.cleStateTransitionOptions,
    selectedRegion?.jurisdiction?.clePeriod,
    selectedRegion?.jurisdiction?.key,
    selectedRegion?.customerId,
    selectedRegion?.creditRegion_SK,
    selectedRegion?.creditRegionShortDescription,
    switchStatusLazy,
    getComplianceStartEndDate,
    lastTrackerRequest,
    activelyViewingUserSk,
    showFailMessage
  ]);

  const saveCompliancePeriod = useCallback(
    async (
      period?: ICLEPeriod,
      complianceGroupSK?: number,
      complianceGroupDescription?: string | null,
      ignoreSuccessMessage?: boolean
    ) => {
      setIsLoading(true);

      const currentPeriod: CLEPeriod = {
        ...selectedClePeriod,
        ...(period ?? {}),
        isUserSet: isUserSet,
        ruleSetFlag: (selectedClePeriod ?? period)?.ruleSetFlag
      };

      try {
        await updateUserOptions({
          customerId: selectedRegion?.customerId,
          jurisdictionId: selectedRegion?.creditRegion_SK,
          jurisdictionKey: selectedRegion?.jurisdiction?.key,
          complianceGroupDescription: complianceGroupDescription,
          complianceGroupSk: complianceGroupSK,
          period: currentPeriod,
          reportingPeriodType: reportingPeriod
        } as CreditTrackerUpdateRequest)
          .unwrap()
          .then(() => !ignoreSuccessMessage && showSuccessMessage('Compliance period updated'));
      } catch (error) {
        setIsLoading(false);
        showFailMessage('There was an error updating compliance period');
      }
    },
    [
      setIsLoading,
      selectedClePeriod,
      isUserSet,
      updateUserOptions,
      selectedRegion?.customerId,
      selectedRegion?.creditRegion_SK,
      selectedRegion?.jurisdiction?.key,
      reportingPeriod,
      showSuccessMessage,
      showFailMessage
    ]
  );

  const updateDateInterval = useCallback(
    async (period?: ICLEPeriod, complianceGroup?: IComplianceGroup) => {
      const currentPeriod: CLEPeriod = {
        ...selectedClePeriod,
        ...(period ?? {}),
        ruleSetFlag: selectedClePeriod?.ruleSetFlag
      };
      try {
        setIsUpdatingDateRange(true);
        const updatePeriod = await updateComplianceDateRange({
          customerId: selectedRegion?.customerId,
          jurisdictionId: selectedRegion?.creditRegion_SK,
          jurisdictionKey: selectedRegion?.jurisdiction?.key,
          complianceGroupDescription: complianceGroup?.shortDescription,
          complianceGroupSk: complianceGroup?.sk,
          period: currentPeriod,
          reportingPeriodType: reportingPeriod
        } as CreditTrackerUpdateRequest);
        if (updatePeriod.error) {
          setIsUpdatingDateRange(false);
          return;
        } else {
          setFieldsWithErrors({});
        }
        const response = await getComplianceStartEndDate([
          { ...lastTrackerRequest, individualSk: activelyViewingUserSk }
        ] as UserTrackerDataRequest[]);
        const foundTrackerData = findInRecordCollection(
          response.data?.trackerUsers ?? [],
          (x: UserTrackerData) =>
            x.creditRegionShortDescription == selectedRegion?.creditRegionShortDescription
        );
        setIsUpdatingDateRange(false);
        return foundTrackerData;
      } catch (error) {
        setIsLoading(false);
        console.error('There was an error updating compliance date interval');
      }
    },
    [
      selectedClePeriod,
      updateComplianceDateRange,
      selectedRegion?.customerId,
      selectedRegion?.creditRegion_SK,
      selectedRegion?.jurisdiction?.key,
      selectedRegion?.creditRegionShortDescription,
      reportingPeriod,
      getComplianceStartEndDate,
      lastTrackerRequest,
      activelyViewingUserSk,
      setFieldsWithErrors,
      setIsLoading
    ]
  );

  const handleResetOnClose = useCallback(async () => {
    if (specialStatusChanged) {
      updateSpecialStatus();
    } else if (JSON.stringify(complianceFormFields) !== JSON.stringify(compliancePeriod)) {
      await saveCompliancePeriod(
        {
          ...complianceFormFields,
          ruleSetFlag: complianceFormFields?.ruleSetFlag
        },
        complianceFormFields?.complianceGroup?.sk,
        complianceFormFields?.complianceGroup?.longDescription,
        true
      );
    }
  }, [
    complianceFormFields,
    compliancePeriod,
    saveCompliancePeriod,
    specialStatusChanged,
    updateSpecialStatus
  ]);

  /** @private */
  const updateStoredData = useCallback((tracker?: UserTrackerData) => {
    if (!tracker) return;

    const newComplianceGroup = tracker?.complianceGroups?.find(
      x => x.sk === tracker?.complianceGroup_SK
    );

    const selectedClePeriod = findSelectedClePeriod(tracker.jurisdiction?.clePeriod);
    const startDate = new Date(selectedClePeriod?.startDate ?? '');
    const endDate = new Date(selectedClePeriod?.endDate ?? '');
    setCompliancePeriod(prev => ({
      ...prev,
      complianceGroup: { ...newComplianceGroup },
      startDate: isValid(startDate) ? format(startDate, 'MM/dd/yyyy') : '',
      endDate: isValid(endDate) ? format(endDate, 'MM/dd/yyyy') : '',
      defaultStartDate: selectedClePeriod?.defaultStartDate,
      defaultEndDate: selectedClePeriod?.defaultEndDate,
      hasDefaultDate: selectedClePeriod?.hasDefaultDate
    }));
  }, []);

  const handleResetComplianceDates = useCallback(async () => {
    const updatedDates = await updateDateInterval(
      {
        ...compliancePeriod,
        isUserSet: false,
        ruleSetFlag: [...new Set([...(selectedClePeriod?.ruleSetFlag ?? []), '!RESET'])]
      },
      compliancePeriod?.complianceGroup as IComplianceGroup
    );
    updateStoredData(updatedDates);
  }, [compliancePeriod, selectedClePeriod?.ruleSetFlag, updateDateInterval, updateStoredData]);

  const waitForReportingPeriodTypeRequired = useCallback(
    (key: ComplianceFieldNames, value: string | number | IComplianceGroup | undefined) => {
      if (!value) return true;

      switch (reportingPeriod) {
        /*Birthday + Admission date */
        case ReportingPeriodType._2: {
          if (
            key == ComplianceFieldNames.ADMIT_DATE &&
            !selectedRegion?.jurisdiction?.isTransitionalOrNewlyAdmitted
          ) {
            return !compliancePeriod?.birthDay || !compliancePeriod.birthMonth;
          }
          if (key == ComplianceFieldNames.BIRTH_DAY) {
            return !compliancePeriod?.admitDate || !compliancePeriod.birthMonth;
          }
          if (key == ComplianceFieldNames.BIRTH_MONTH) {
            return !compliancePeriod?.admitDate || !compliancePeriod.birthDay;
          }
          return false;
        }
        /*Birthday */
        case ReportingPeriodType._3: {
          if (key == ComplianceFieldNames.BIRTH_DAY) {
            return !compliancePeriod.birthMonth;
          }
          if (key == ComplianceFieldNames.BIRTH_MONTH) {
            return !compliancePeriod.birthDay;
          }
          return false;
        }
        /*Compliance Group + Admission Date */
        case ReportingPeriodType._5: {
          if (key == ComplianceFieldNames.COMPLIANCE_GROUP) {
            return !compliancePeriod?.admitDate;
          }
          if (key == ComplianceFieldNames.ADMIT_DATE) {
            return !compliancePeriod?.complianceGroup;
          }
          return false;
        }
      }
      return false;
    },
    [
      compliancePeriod?.admitDate,
      compliancePeriod.birthDay,
      compliancePeriod.birthMonth,
      compliancePeriod?.complianceGroup,
      reportingPeriod,
      selectedRegion?.jurisdiction?.isTransitionalOrNewlyAdmitted
    ]
  );

  const handleChange = useCallback(
    async (key: ComplianceFieldNames, value: string | number | IComplianceGroup | undefined) => {
      const newPeriod: ComplianceFormFields = {
        ...compliancePeriod,
        [key]: value,
        isUserSet: key == ComplianceFieldNames.START_DATE || key == ComplianceFieldNames.END_DATE
      };

      setCompliancePeriod(newPeriod);

      if (
        key == ComplianceFieldNames.ADMIT_DATE &&
        (typeof value === 'string' || value instanceof String)
      ) {
        const date = new Date(value as string);
        if (value.length != 10 || date.getFullYear() < 1900) {
          return;
        }
      }

      if (
        key == ComplianceFieldNames.START_DATE ||
        key == ComplianceFieldNames?.END_DATE ||
        waitForReportingPeriodTypeRequired(key, value)
      )
        return;

      const updatedDates = await updateDateInterval(
        { ...newPeriod, [key]: value },
        newPeriod?.complianceGroup as IComplianceGroup
      );
      updateStoredData(updatedDates);
    },
    [compliancePeriod, updateDateInterval, updateStoredData, waitForReportingPeriodTypeRequired]
  );

  const handleSaveButton = useCallback(
    async (displaySuccessMessage: boolean = true) => {
      setIsSubmitting(true);
      if (handleValidation(isTransitionalOrNewlyAdmittedRegion)) {
        await saveCompliancePeriod(
          compliancePeriod,
          compliancePeriod.complianceGroup?.sk,
          compliancePeriod.complianceGroup?.longDescription,
          !displaySuccessMessage
        );
        setOpen(false);
        setIsSubmitting(false);
        return true;
      }
      setIsSubmitting(false);
      return false;
    },
    [
      compliancePeriod,
      handleValidation,
      isTransitionalOrNewlyAdmittedRegion,
      saveCompliancePeriod,
      setOpen
    ]
  );

  const showReset = useMemo(() => {
    return (
      !isUsingDefaultDates(compliancePeriod) &&
      !isMissingCompliancePeriod &&
      !!selectedRegion?.jurisdiction?.jurisdictionReportingPeriod &&
      !!compliancePeriod?.isUserSet
    );
  }, [
    compliancePeriod,
    isMissingCompliancePeriod,
    selectedRegion?.jurisdiction?.jurisdictionReportingPeriod
  ]);

  const handleSelectorChange = useCallback(
    (status: SpecialStatusSelector) => {
      setStatuses(
        statuses.map(a => ({
          ...a,
          value: `${a.text === status.text}`
        }))
      );
    },
    [setStatuses, statuses]
  );

  const handleSpecialStatusValidation = useCallback(async () => {
    const anySelected = statuses.some(status => status.value.toString()?.toLowerCase() === 'true');
    if (anySelected) {
      const foundTrackerData = await updateSpecialStatus();

      if (foundTrackerData) {
        const foundSelectedPeriod = findSelectedClePeriod(
          foundTrackerData?.jurisdiction?.clePeriod
        );

        if (
          !periodIsMissingComplianceInformation(
            foundSelectedPeriod,
            foundTrackerData?.jurisdiction?.requireSetup
          )
        ) {
          const periodAdmitDate = getPeriodAdmitDate(foundSelectedPeriod ?? {}),
            periodStartDate = getPeriodStartDate(foundSelectedPeriod ?? {}),
            periodEndDate = getPeriodEndDate(foundSelectedPeriod ?? {}),
            birthDate = getUserBirthDate(
              foundTrackerData?.jurisdiction?.userComplianceInformation?.birthDate ?? ''
            );

          setCompliancePeriod({
            ...foundSelectedPeriod,
            complianceGroup: foundTrackerData?.complianceGroups?.find(
              x => x.sk === foundTrackerData?.complianceGroup_SK
            ),
            birthMonth: (birthDate?.getMonth() ?? 0) + 1,
            birthDay: birthDate?.getDate() ?? 0,
            admitDate: periodAdmitDate ? format(periodAdmitDate, 'MM/dd/yyyy') : undefined,
            startDate: periodStartDate ? format(periodStartDate, 'MM/dd/yyyy') : '',
            endDate: periodEndDate ? format(periodEndDate, 'MM/dd/yyyy') : ''
          });
        } else {
          setCompliancePeriod({
            ...emptyComplianceFormFields,
            ruleSetFlag: foundTrackerData.jurisdiction?.clePeriod?.ruleSetFlag ?? []
          });
        }
      }
      setStepIndex(stepIndex + 1);
    } else {
      setShowSpecialStatusError(true);
    }
  }, [emptyComplianceFormFields, statuses, stepIndex, updateSpecialStatus]);

  return {
    setCompliancePeriod,
    compliancePeriod,
    handleChange,
    handleResetOnClose,
    handleSaveButton,
    handleValidation,
    handleResetComplianceDates,
    fieldsWithErrors,
    setFieldsWithErrors,
    reportingPeriod,
    isSubmitting,
    isTransitionalOrNewlyAdmittedRegion,
    isUpdatingDateRange,
    isUpdatingForm,
    showReset,
    selectedRegion,
    stepIndex,
    setStepIndex,
    showSuccessMessage,
    showFailMessage,
    specialStatus: {
      statuses,
      showSpecialStatusError,
      setStatuses,
      updateSpecialStatus,
      handleSelectorChange,
      handleSpecialStatusValidation
    }
  };
};
export default useComplianceModal;
